import { getChainDateTime, } from "@bracket/tenderly/time";
import { lock_date, } from "@store/commons";
import type { IBracketStore, IdentifiableVaultWithState, LoadedVault, LoadedVaultWithPnl, } from "@types";
import { applySynthTransactions2, applyVaultModifiers, getVaultExposure, getVaultMeta, gql, purgeCache, } from "@vault/resolvers";
import { DateTime, } from "luxon";
import { applyBalances, } from "./applyBalances";
import { WEI, } from "@bracket/blocksmith";
import { getNavData, } from "../meta/getNavData";





/**
 * Loads a vault with full data including metadata, exposure, and PnL calculations
 * 
 * @param {IdentifiableVaultWithState} vault - The vault identifier with state information
 * @param {boolean} [force=false] - If true, forces reload even if vault is already loaded
 * @returns {Promise<LoadedVaultWithPnl>} Promise resolving to a fully loaded vault with PnL data
 * @throws {Error} Error if vault cannot be loaded or is not found
 */
export const getLoadedVault = async (store: IBracketStore, vault: IdentifiableVaultWithState, force = false, ): Promise<LoadedVaultWithPnl> => {

	try {

		if(!vault)
			throw new Error("failed gaining vault, no identifier provided");

		if(!force && ["LOADED", "MANAGEABLE"].includes(vault.state)) {
			return vault as LoadedVaultWithPnl;
		}

		if(force) {
			purgeCache("VAULT_DATA", vault.symbol);
		}

		const {
			wallet: {
				vaults,
			}
		} = await gql<"VAULT_DATA">("VAULT_DATA", vault.symbol as string);

		const loaded = vaults.find(v => v.symbol === vault.symbol);
		if(!loaded)
			throw new Error(`failed gaining vault "${vault.symbol}"`);

		const scheduled = await applyScheduleInformation(loaded);
		
		if(scheduled) {
			const state:|"LOADED" = "LOADED";
			const vault = {
				...scheduled,
				state: state,
				meta: await getVaultMeta(store, { ...scheduled, state: "LOADED", }),
				exposure: await getVaultExposure(store, { ...scheduled, state: "LOADED", }),
			};

			const withNavData = await getNavData(vault);
			const synthesized = applySynthTransactions2(withNavData);
			const withBalances = await applyBalances(synthesized);
			const applied = await applyVaultModifiers(withBalances);


			applied.closed = applied.symbol === "BBOR1";

			const result = applyWEIInstances(applied);

			return result;

		}
		throw new Error(`failed gaining vault "${vault.symbol}"`);

	} 
	catch(err) {
		
		throw new Error(`getLoadedVault failed for vault ${vault.symbol} - ${err instanceof Error ? err.message : "without error instance"}`);
	}
};

const applyScheduleInformation = async ( vault: LoadedVault ): Promise<LoadedVault> => {

	const { launch_date, expected_lock_hours, epoch_duration_days, withdraw_delay_days, epoch, epoch_offset_hours, } = vault;

	// Set deposit_type based on launch_date
	if(launch_date) {

		try {

			const now = await getChainDateTime();
			const duration = epoch_duration_days > 1 ? `${epoch_duration_days} days` : `day`;
			const launchDateTime = DateTime.fromISO(launch_date).plus({ hours: epoch_offset_hours, });
			const locked = lock_date(vault);
			const isLaunchDay = locked.hasSame(now, "day");
			const dayOfWeek = epoch === 0 && !isLaunchDay || epoch_duration_days > 1 ? launchDateTime.weekdayLong : "Today";

			vault.schedulable_block = vault.navs.find(n => n.epoch === 1)?.block || Infinity;
			vault.deposit_type = `Request prior to ${locked.toLocaleString(DateTime.TIME_SIMPLE)} ${dayOfWeek}`;
			vault.withdrawal_type = `Request prior to ${locked.toLocaleString(DateTime.TIME_SIMPLE)} ${dayOfWeek}, available ${withdraw_delay_days + Math.floor(expected_lock_hours / 24)} day${withdraw_delay_days > 1 ? "s" : ""} later`;
			vault.nav_update_schedule = `Updated every ${duration} ${locked.toLocaleString(DateTime.TIME_SIMPLE)} ${epoch_duration_days > 1 ? dayOfWeek : ""}`;

		}
		catch(err) {

			console.warn("Failed to format deposit_type from launch_date:", err);
			vault.deposit_type = "";
			vault.withdrawal_type = "";
			vault.nav_update_schedule = "";

		}

	}

	return vault as LoadedVault;

}

const applyWEIInstances = ( vault: LoadedVaultWithPnl ): LoadedVaultWithPnl & { mopho_data: { lltv: WEI, leverage: WEI, leveragedYield: WEI, } | null; } => {


	if(!vault.morpho_data)
		return vault;

	return {
		...vault,
		morpho_data: {
			lltv: WEI.fromSync(vault.morpho_data.lltv, 18),
			leverage: WEI.fromSync(vault.morpho_data.leverage, 18),
			leveragedYield: WEI.fromSync(vault.morpho_data.leveragedYield, 18),
			borrowYield: WEI.fromSync(vault.morpho_data.borrowYield, 18),
		}
	}

}
